import { useEffect, useState } from "react";
import { api, formatApiError, formatFCFA, formatDate, downloadBlob } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, CheckCircle2, Circle, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import PhotoUpload from "@/components/PhotoUpload";

const MEMBER_TYPES = [
  { value: "adherent", label: "Adhérent" },
  { value: "joueur", label: "Joueuse" },
  { value: "dirigeant", label: "Dirigeant" },
  { value: "benevole", label: "Bénévole" },
  { value: "supporter", label: "Supporter" },
];

const EMPTY = {
  first_name: "", last_name: "", member_type: "adherent",
  date_of_birth: "", phone: "", email: "", address: "", license_number: "",
  join_date: new Date().toISOString().slice(0, 10),
  cotisation_amount: 5000, photo_url: "",
};

export default function Membres() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/members").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (m) => { setEditing(m); setForm({ ...EMPTY, ...m }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, cotisation_amount: Number(form.cotisation_amount) || 0 };
      if (editing) {
        await api.patch(`/members/${editing.id}`, payload);
        toast.success("Membre mis à jour");
      } else {
        await api.post("/members", payload);
        toast.success("Membre inscrit");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const togglePaid = async (m) => {
    try {
      await api.patch(`/members/${m.id}`, {
        cotisation_paid: !m.cotisation_paid,
        cotisation_date: !m.cotisation_paid ? new Date().toISOString().slice(0, 10) : null,
      });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/members/${id}`);
    load();
  };

  const generateCard = async (m) => {
    try {
      const res = await api.post("/documents/generate", {
        document_type: "carte_membre",
        data: {
          member_name: `${m.first_name} ${m.last_name}`,
          member_type: MEMBER_TYPES.find((t) => t.value === m.member_type)?.label || m.member_type,
          license_number: m.license_number || "—",
          photo_url: m.photo_url,
          valid_until: "31/12/2026",
        },
      }, { responseType: "blob" });
      downloadBlob(res.data, `carte_membre_${m.last_name}_${m.first_name}.pdf`, "application/pdf");
      toast.success("Carte de membre générée");
    } catch (e) {
      toast.error("Erreur lors de la génération");
    }
  };

  const exportCSV = () => {
    const headers = ["Nom", "Prénom", "Type", "Téléphone", "Email", "Cotisation", "Payée"];
    const rows = items.map((m) => [
      m.last_name, m.first_name, m.member_type, m.phone || "", m.email || "",
      m.cotisation_amount, m.cotisation_paid ? "Oui" : "Non",
    ]);
    const csv = [headers, ...rows].map((r) =>
      r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `membres-amalvbc.csv`;
    a.click();
  };

  return (
    <div data-testid="membres-page">
      <PageHeader title="Gestion des membres" subtitle="Adhérents, joueuses, bénévoles, supporters et partenaires">
        <Button onClick={exportCSV} variant="outline" data-testid="export-members-button">
          <Download className="h-4 w-4 mr-2" /> Exporter
        </Button>
        <Button onClick={startCreate} data-testid="add-member-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvelle inscription
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full data-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                <th className="text-left p-4">Nom complet</th>
                <th className="text-left p-4">Type</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-left p-4">Inscription</th>
                <th className="text-right p-4">Cotisation</th>
                <th className="text-center p-4">Statut</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-slate-400">Aucun membre.</td></tr>
              ) : items.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-b border-slate-100">
                  <td className="p-4">
                    <div className="font-semibold text-slate-900">{m.first_name} {m.last_name}</div>
                    {m.license_number && <div className="text-xs text-slate-400">N° {m.license_number}</div>}
                  </td>
                  <td className="p-4">
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 capitalize">
                      {MEMBER_TYPES.find((t) => t.value === m.member_type)?.label}
                    </Badge>
                  </td>
                  <td className="p-4 text-xs text-slate-600">
                    <div>{m.phone || "—"}</div>
                    <div className="text-slate-400">{m.email || ""}</div>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{formatDate(m.join_date)}</td>
                  <td className="p-4 text-right font-mono text-slate-900">{formatFCFA(m.cotisation_amount || 0)}</td>
                  <td className="p-4 text-center">
                    <button onClick={() => togglePaid(m)} data-testid={`toggle-paid-${m.id}`}>
                      {m.cotisation_paid ? (
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 mx-auto" />
                      ) : (
                        <Circle className="h-5 w-5 text-slate-300 mx-auto" />
                      )}
                    </button>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => generateCard(m)}
                        data-testid={`card-member-${m.id}`}
                        className="text-emerald-700 h-8" title="Carte de membre">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => startEdit(m)}
                        data-testid={`edit-member-${m.id}`} className="h-8 w-8 p-0">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(m.id)}
                        data-testid={`delete-member-${m.id}`}
                        className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">
              {editing ? "Modifier le membre" : "Nouvelle inscription"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prénom" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testid="mem-firstname" />
            <Field label="Nom" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testid="mem-lastname" />
            <SelectField label="Catégorie" value={form.member_type}
              onChange={(v) => setForm({ ...form, member_type: v })}
              options={MEMBER_TYPES} testid="mem-type" />
            <Field label="Date de naissance" type="date" value={form.date_of_birth}
              onChange={(v) => setForm({ ...form, date_of_birth: v })} testid="mem-dob" />
            <Field label="Téléphone" value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })} testid="mem-phone" />
            <Field label="Email" type="email" value={form.email}
              onChange={(v) => setForm({ ...form, email: v })} testid="mem-email" />
            <Field label="Adresse" value={form.address}
              onChange={(v) => setForm({ ...form, address: v })} testid="mem-address" />
            <Field label="N° de licence" value={form.license_number}
              onChange={(v) => setForm({ ...form, license_number: v })} testid="mem-license" />
            <Field label="Date d'inscription" type="date" value={form.join_date}
              onChange={(v) => setForm({ ...form, join_date: v })} testid="mem-joindate" />
            <Field label="Cotisation (FCFA)" type="number" value={form.cotisation_amount}
              onChange={(v) => setForm({ ...form, cotisation_amount: v })} testid="mem-cotisation" />
            <div className="col-span-2">
              <PhotoUpload value={form.photo_url}
                onChange={(v) => setForm({ ...form, photo_url: v })}
                testid="mem-photo" label="Photo (pour la carte de membre)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-member-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
